// ============================================================================
// BuildingActionCard — every building action's card (batch 3 of item 944),
// to Ben's approved card demo (docs/build-room/demos/item-944.html): the
// action and the building in the title, the fields the screen asks for in the
// screen's own words, "Now …" under a field that changes something, the
// read-back line and a button naming the consequence on a card that moves
// money or cannot be undone, Do it at the bottom, every control ≥ 44 px, and
// the card locked once it is saved. What each action shows is buildingCard()
// in ../building-actions.ts; the save is commitBuildingAction() there.
// ============================================================================

import { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { feedbackFlagDefaults } from '../../account-management/lib/building-saves';
import { buildingCard, buildingOf, initialCardFields, type BuildingDraft, type FieldSpec } from '../building-actions';
import { CardShell } from './card-shell';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-base text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

export function BuildingActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as BuildingDraft;
  const start = useMemo(() => initialCardFields(draft), [draft]);
  const [fields, setFields] = useState<Record<string, unknown>>(start);
  const card = buildingCard(draft, fields);
  const here = buildingOf(draft);
  const wasEdited = JSON.stringify(fields) !== JSON.stringify(start);

  const set = (key: string, value: unknown) =>
    setFields((prev) => {
      const next = { ...prev, [key]: value };
      // The feedback form pre-ticks the two payment flags from the sentiment.
      if (draft.action === 'log_feedback' && key === 'sentiment') {
        const d = feedbackFlagDefaults(String(value));
        next.counts_against_no_miss = d.counts_against_nmi;
        next.counts_toward_compliment = d.counts_toward_compliment;
      }
      return next;
    });

  const isStopService = ['acknowledge_termination', 'approve_termination', 'set_termination_final_date', 'cancel_termination'].includes(draft.action);
  const buildingHref = `/account-management/facilities/${draft.facility_id}${isStopService ? '/stop-service' : ''}`;

  return (
    <CardShell
      title={`${card.title} · ${here}`}
      icon={<Building2 className="h-4 w-4" />}
      accentClass={card.readBack ? 'border-st-warn-border-strong' : 'border-tcs-border'}
      status={proposal.status}
      committedLabel={
        proposal.result
          ? proposal.result.done < proposal.result.of
            ? `Closed ${proposal.result.done} of ${proposal.result.of} — the rest were not yours to close`
            : `Closed ${proposal.result.done}`
          : 'Saved'
      }
      isCommitting={isCommitting}
      doItDisabled={!!card.missing}
      doItDisabledReason={card.missing ?? undefined}
      readBack={card.readBack ?? undefined}
      doItLabel={card.doItLabel ?? undefined}
      onDoIt={() => onDoIt({ ...draft, fields }, wasEdited)}
      onDiscard={onDiscard}
      after={
        <Link to={buildingHref} className="text-sm font-medium text-tcs-text underline">
          {isStopService ? 'Open the Stop Service page' : 'Open the building'}
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}

      {card.rows && card.rowsKey && (
        <ul className="space-y-1">
          {card.rows.map((r) => {
            const ids = (fields[card.rowsKey!] as string[] | undefined) ?? [];
            const on = ids.includes(r.id);
            return (
              <li key={r.id}>
                <label className="flex min-h-[44px] items-start gap-2 rounded-md border border-tcs-border px-2 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => set(card.rowsKey!, on ? ids.filter((x) => x !== r.id) : [...ids, r.id])}
                    className="mt-0.5 h-5 w-5"
                  />
                  <span className="text-tcs-text">{r.text}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {card.fields.map((spec) => (
        <FieldControl key={spec.key} spec={spec} value={fields[spec.key]} onChange={(v) => set(spec.key, v)} />
      ))}
    </CardShell>
  );
}

export function FieldControl({ spec, value, onChange }: { spec: FieldSpec; value: unknown; onChange: (v: unknown) => void }) {
  const now = spec.now != null ? <span className="mt-1 block text-xs text-tcs-text-muted">Now: {spec.now}</span> : null;
  if (spec.kind === 'boolean') {
    return (
      <label className="flex min-h-[44px] items-start gap-2 rounded-md border border-tcs-border px-2 py-2 text-sm text-tcs-text">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5" />
        <span>
          {spec.label}
          {now}
        </span>
      </label>
    );
  }
  const v = value == null ? '' : String(value);
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-tcs-text-muted">
        {spec.label}
        {spec.required ? '' : ' (optional)'}
      </span>
      {spec.kind === 'select' ? (
        <select value={v} onChange={(e) => onChange(e.target.value || null)} className={inputClass}>
          {!spec.required && <option value="">— none —</option>}
          {spec.required && !spec.options?.some((o) => o.value === v) && <option value="">Pick one…</option>}
          {(spec.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : spec.kind === 'textarea' ? (
        <textarea value={v} onChange={(e) => onChange(e.target.value)} rows={3} className={`${inputClass} resize-y`} />
      ) : (
        <input
          type={spec.kind === 'date' ? 'date' : spec.kind === 'number' ? 'number' : spec.kind === 'datetime' ? 'datetime-local' : 'text'}
          inputMode={spec.kind === 'number' ? 'decimal' : undefined}
          value={spec.kind === 'date' ? v.slice(0, 10) : spec.kind === 'datetime' ? v.slice(0, 16) : v}
          onChange={(e) =>
            onChange(spec.kind === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)
          }
          className={inputClass}
        />
      )}
      {now}
    </label>
  );
}
